import React, { useEffect, useState } from 'react';
import { X, ExternalLink, User, Calendar, PlayCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface ConfirmationPageProps {
  onContinue: () => void;
}

interface RelatedSequence {
  id: string;
  title: string;
  artist: string;
  price: number;
  genre?: string;
  year_introduced?: number;
  isFree?: boolean;
}

/**
 * ConfirmationPage appears as the final step in the download flow.
 * Shows download completion confirmation and suggests related sequences.
 */
export const ConfirmationPage: React.FC<ConfirmationPageProps> = ({
  onContinue,
}) => {
  const { user } = useAuth();
  const { 
    currentSession,
    modalState,
    hideConfirmationPage,
    hideAllModals
  } = useDownloadFlowStore();

  const [relatedSequences, setRelatedSequences] = useState<RelatedSequence[]>([]);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);

  // Load related sequences based on the downloaded sequence
  useEffect(() => {
    const loadRelatedSequences = async () => {
      if (!currentSession?.sequenceId) return;

      setIsLoadingRelated(true);
      try {
        // Query for sequences from the same artist or similar genre
        const { data, error } = await supabase
          .from('display_songs')
          .select('id, title, artist, sequence_price, genre, year_introduced')
          .neq('id', currentSession.sequenceId)
          .or(`artist.ilike.%${currentSession.sequenceArtist}%,genre.ilike.%music%`)
          .eq('sequence_available', true)
          .limit(3);

        if (error) {
          console.error('Error loading related sequences:', error);
          return;
        }

        const sequences: RelatedSequence[] = (data || []).map(seq => ({
          id: seq.id,
          title: seq.title,
          artist: seq.artist,
          price: seq.sequence_price || 0,
          genre: seq.genre,
          year_introduced: seq.year_introduced,
          isFree: !seq.sequence_price || seq.sequence_price === 0,
        }));

        setRelatedSequences(sequences);
      } catch (error) {
        console.error('Failed to load related sequences:', error);
      } finally {
        setIsLoadingRelated(false);
      }
    };

    if (modalState.isConfirmationPageVisible) {
      loadRelatedSequences();
    }
  }, [currentSession?.sequenceId, currentSession?.sequenceArtist, modalState.isConfirmationPageVisible]);

  // Handle modal close
  const handleClose = () => {
    hideConfirmationPage();
    onContinue();
  };

  // Navigate to a related sequence
  const handleViewSequence = (sequenceId: string) => {
    hideAllModals();
    window.open(`/sequence/${sequenceId}`, '_blank');
  };

  // Prevent scroll when modal is open
  useEffect(() => {
    if (modalState.isConfirmationPageVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [modalState.isConfirmationPageVisible]);

  if (!modalState.isConfirmationPageVisible || !currentSession) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-title"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-green-600" />
            </div>
            <h2 id="confirmation-title" className="text-xl font-semibold text-gray-900">
              Download Complete!
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close confirmation"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Success Message */}
          <div className="text-center space-y-3">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <PlayCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              "{currentSession.sequenceTitle}" is ready to light up your display!
            </h3>
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <User className="h-4 w-4" />
              <span>{currentSession.sequenceArtist}</span>
            </div>
          </div>

          {/* Download Info */}
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-4 w-4 rounded-full bg-green-600 flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
              <span className="text-green-800 font-medium">Successfully downloaded and added to your collection!</span>
            </div>
            <p className="text-green-700 text-sm">
              Your sequence file is now available in your downloads folder and has been added to your {new Date().getFullYear()} display list.
            </p>
          </div>

          {/* Related Sequences */}
          {relatedSequences.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h4 className="text-md font-medium text-gray-900">You might also like:</h4>
                <Badge variant="secondary" className="text-xs">
                  {currentSession.sequenceArtist}
                </Badge>
              </div>
              
              <div className="grid gap-3">
                {relatedSequences.map((sequence) => (
                  <Card key={sequence.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-gray-900 truncate">
                            {sequence.title}
                          </h5>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                            <User className="h-3 w-3" />
                            <span>{sequence.artist}</span>
                            {sequence.year_introduced && (
                              <>
                                <Calendar className="h-3 w-3 ml-2" />
                                <span>{sequence.year_introduced}</span>
                              </>
                            )}
                          </div>
                          {sequence.genre && (
                            <Badge variant="outline" className="mt-2 text-xs">
                              {sequence.genre}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 ml-4">
                          <div className="text-right">
                            <div className="font-medium text-gray-900">
                              {sequence.isFree ? 'Free' : `$${sequence.price.toFixed(2)}`}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewSequence(sequence.id)}
                            className="shrink-0"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {isLoadingRelated && (
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 text-gray-600">
                <div className="h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                <span className="text-sm">Finding related sequences...</span>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 p-6 pt-0">
          <Button
            onClick={() => {
              hideAllModals();
              window.open('/marketplace', '_blank');
            }}
            variant="outline"
            className="flex-1"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Browse More Sequences
          </Button>
          <Button
            onClick={handleClose}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage; 